import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { PatientForm } from '@principle-theorem/principle-core';
import {
  PatientFormStatus,
  PatientFormType,
  type IPatient,
  type IPatientForm,
} from '@principle-theorem/principle-core/interfaces';
import { Firestore, isSameRef, type WithRef } from '@principle-theorem/shared';
import { type Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

export interface IPatientFormState {
  patient: WithRef<IPatient> | undefined;
  form: WithRef<IPatientForm> | undefined;
  relatedForms: WithRef<IPatientForm>[];
  loading: boolean;
  patientUpdates: Partial<IPatient>;
}

const initialState: IPatientFormState = {
  patient: undefined,
  form: undefined,
  relatedForms: [],
  loading: true,
  patientUpdates: {},
};

function isReviewablePatientDetailsForm(form?: WithRef<IPatientForm>): boolean {
  if (!form) {
    return false;
  }
  return (
    PatientForm.isPatientDetailsForm(form) &&
    form.status === PatientFormStatus.Submitted
  );
}

@Injectable()
export class PatientFormStore extends ComponentStore<IPatientFormState> {
  readonly patient$ = this.select((store) => store.patient);
  readonly form$ = this.select((store) => store.form);
  readonly isPatientDetails$ = this.select(
    this.form$,
    (form) => form?.formType === PatientFormType.PatientDetailsForm
  );
  readonly isCustomForm$ = this.select(
    this.form$,
    (form) => form?.formType === PatientFormType.CustomForm
  );
  /**
   * Printing renders the patient's own answers, so a form still waiting on the
   * patient has nothing to print. An issued form is therefore not printable,
   * and printing is not an alternative way of getting a form to a patient.
   */
  readonly canPrint$ = this.select(
    this.form$,
    this.isCustomForm$,
    (form, isCustomForm) =>
      isCustomForm && form?.status !== PatientFormStatus.Issued
  );
  readonly relatedForms$ = this.select((store) => store.relatedForms);
  readonly isLatest$ = this.select(
    this.form$,
    this.relatedForms$,
    (form, relatedForms) => {
      const latest = PatientForm.latest(relatedForms);
      return form && latest && isSameRef(form, latest);
    }
  );
  readonly loading$ = this.select((store) => store.loading);
  /**
   * Only a submitted Patient Details form is editable; everything else,
   * including no form loaded yet, is view-only.
   */
  readonly readonly$ = this.select(
    this.form$,
    (form) => !isReviewablePatientDetailsForm(form)
  );
  readonly patientUpdates$ = this.select((store) => store.patientUpdates);
  readonly emptyState$ = this.select(
    this.loading$,
    this.relatedForms$,
    (loading, forms) => !loading && !forms.length
  );

  readonly loadForm = this.effect(
    (form$: Observable<WithRef<IPatientForm> | undefined>) => {
      return form$.pipe(
        switchMap((submittedForm) => this._loadForm(submittedForm))
      );
    }
  );

  readonly loadRelatedForms = this.effect(
    (forms$: Observable<WithRef<IPatientForm>[]>) => {
      return forms$.pipe(
        tap((relatedForms) => {
          this.patchState({ relatedForms });
        })
      );
    }
  );

  constructor() {
    super(initialState);
  }

  setPatientDetailsUpdates(patientDetailsUpdates: Partial<IPatient>): void {
    this.patchState({ patientUpdates: patientDetailsUpdates });
  }

  private async _loadForm(form?: WithRef<IPatientForm>): Promise<void> {
    if (!form) {
      return this.patchState({
        form: undefined,
        loading: false,
        relatedForms: [],
      });
    }
    const patient = await Firestore.getDoc(PatientForm.patientRef(form));
    const patientUpdates = PatientForm.isPatientDetailsForm(form)
      ? form.form.data
      : {};

    this.patchState({ patient, form, patientUpdates, loading: false });
    this.loadRelatedForms(PatientForm.relatedForms$(form));
  }
}
